import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Table of contents ---------------- */

const TOC = [
  { id: "what-makes-a-record-different", title: "What Makes a Digital Asset Record Different" },
  { id: "blockchain-data-to-capture", title: "Blockchain Data Finance Teams Need to Capture" },
  { id: "asset-and-network-details", title: "Asset and Network Details in Digital Asset Records" },
  { id: "building-a-complete-record", title: "Building a Complete Digital Asset Transaction Record" },
  { id: "valuing-digital-assets", title: "Valuing Digital Assets in Financial Records" },
  { id: "financial-statements", title: "How Digital Assets Are Recorded in Financial Statements" },
  { id: "recordkeeping-and-retention", title: "Recordkeeping and Retention Requirements" },
  { id: "travel-rule", title: "Compliance Recordkeeping: The Travel Rule" },
  { id: "common-mistakes", title: "Common Mistakes in Digital Asset Recordkeeping" },
  { id: "practical-example", title: "Building the Record: A Practical Example" },
];

/* ---------------- Data ---------------- */

const RECORD_COMPONENTS = [
  [
    "Transaction details",
    "Asset type, amount, wallet addresses, network, transaction ID, timestamp",
  ],
  [
    "Counterparty information",
    "Who is involved and their relationship to the company. A wallet address alone rarely identifies a supplier, customer, or employee",
  ],
  [
    "Business purpose",
    "The reason for the transaction: contractor payment, supplier settlement, customer refund, treasury transfer, and so on",
  ],
  [
    "Supporting documentation",
    "Invoices, contracts, payment approvals, or other internal records tying the transaction to the business",
  ],
  [
    "Valuation information",
    "The value of the asset at the time of the transaction, and the method used to determine it",
  ],
];

const EXAMPLE_RECORD = [
  ["Transaction hash", "0x8f2a...c91d"],
  ["Network", "Ethereum"],
  ["Amount and asset", "5,000 USDC"],
  ["Timestamp", "2026-03-14, 14:37 UTC"],
  ["Counterparty", "Contractor — [name], per Master Services Agreement dated [date]"],
  ["Business purpose", "Monthly contractor payment, invoice #1042"],
  ["Valuation", "5,000 USDC ≈ 5,000 USD, spot rate at time of transaction"],
  ["Supporting documentation", "Invoice #1042, payment approval, signed contract"],
  ["Accounting treatment", "Recorded at fair value per ASU 2023-08 (or local equivalent)"],
];

/* ---------------- Shared typographic atoms ---------------- */

const P = ({ children, className = "" }) => (
  <p className={`mt-5 text-base leading-[1.75] text-muted-foreground ${className}`}>{children}</p>
);

const Note = ({ children, testId }) => (
  <div
    className="mt-6 rounded-xl border-l-[3px] border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/[0.05] px-6 py-5"
    data-testid={testId}
  >
    <p className="text-[0.95rem] leading-relaxed text-foreground/80">{children}</p>
  </div>
);

const Bullet = ({ lead, children }) => (
  <li className="flex gap-3 text-base leading-relaxed text-muted-foreground">
    <span className="mt-[0.72em] h-[3px] w-3 shrink-0 bg-[hsl(var(--accent))]" aria-hidden="true" />
    <span>
      {lead && <span className="font-medium text-foreground">{lead} </span>}
      {children}
    </span>
  </li>
);

const Section = ({ id, index, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0" data-testid={`article-section-${id}`}>
    <div className="flex items-baseline gap-4">
      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
        {String(index).padStart(2, "0")}
      </span>
      <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
        {title}
      </h2>
    </div>
    <div className="mt-1 border-t border-[hsl(var(--primary))]/10 pt-1">{children}</div>
  </section>
);

/* Two-column editorial table with a stacked mobile fallback */
const RecordTable = ({ rows, headers, testId, firstColWidth = "w-[220px]" }) => (
  <>
    <div className={`mt-6 hidden overflow-hidden rounded-xl ring-1 ring-[hsl(var(--primary))]/12 sm:block`} data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow className="border-[hsl(var(--primary))]/12 bg-[hsl(var(--primary))]/[0.05] hover:bg-[hsl(var(--primary))]/[0.05]">
            <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
              {headers[0]}
            </TableHead>
            <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
              {headers[1]}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([left, right]) => (
            <TableRow key={left} className="border-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/[0.02]">
              <TableCell className={`${firstColWidth} whitespace-normal px-5 py-4 align-top text-sm font-medium leading-relaxed text-foreground`}>
                {left}
              </TableCell>
              <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                {right}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <div className="mt-6 space-y-5 sm:hidden" data-testid={`${testId}-mobile`}>
      {rows.map(([left, right]) => (
        <div key={left} className="border-t border-[hsl(var(--accent))]/70 pt-4">
          <h3 className="text-base font-semibold tracking-[-0.01em] text-foreground">{left}</h3>
          <p className="mt-2 text-base leading-[1.7] text-muted-foreground">{right}</p>
        </div>
      ))}
    </div>
  </>
);

/* ---------------- Hero ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">Digital Asset Records and Reporting</span>
      </nav>

      {/* Hero panel — title, meta, intro, and tags on a soft tinted canvas */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Digital Asset Records and Reporting: What Finance Teams Should Know
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Guide
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            9 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            A company sends 5,000 USDC to an international contractor. Within
            seconds, the transaction appears on the blockchain.{" "}
            <br className="hidden lg:block" />
            The payment has a timestamp, a transaction ID, a sender address,
            and a recipient address.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            From a technical perspective, the transfer is complete. For a
            finance team, however, the work has only started.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Digital Assets", "Accounting"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Sticky table of contents with scrollspy ---------------- */

const TocSidebar = () => {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = TOC[0].id;
      for (const item of TOC) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {TOC.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

/* ---------------- Article body ---------------- */

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {/* Intro copy — outside the hero panel, in the article column */}
          <div data-testid="article-intro-copy">
            <p className="text-base leading-[1.75] text-muted-foreground">
              Several months later, someone reviewing the company's records may
              ask who received the payment, what its business purpose was,
              which invoice or contract it relates to, how it should be
              recorded in the accounting system, and what documentation
              supports it.
            </p>
            <p className="mt-5 text-base leading-[1.75] text-muted-foreground">
              A blockchain transaction can confirm that a digital asset moved
              from one address to another. It does not explain the business
              activity behind that movement. This distinction matters more each
              year as companies use digital assets for payments, treasury
              operations, and other financial workflows.
            </p>
            <p className="mt-5 text-base leading-[1.75] text-muted-foreground">
              This guide covers what digital asset records are, how regulators
              now expect them to be measured and reported, what data finance
              teams should capture, and how a single blockchain transaction
              becomes a complete financial record.
            </p>
          </div>

          {/* 01 — What Makes a Digital Asset Record Different */}
          <Section id="what-makes-a-record-different" index={1} title="What Makes a Digital Asset Record Different">
            <P>
              A digital asset record is the information a company needs to
              track, classify, and report a transaction involving a digital
              asset.
            </P>
            <P>
              A blockchain records the movement of an asset and provides
              technical details about the transfer. It may show that 5,000 USDC
              moved from one wallet address to another, on a specific network,
              at a specific time, with a unique transaction identifier. This
              confirms that the transfer occurred, but it says nothing about
              the business reason behind it. A complete financial record adds
              the identity of the counterparty, the purpose of the payment,
              related invoices or contracts, internal approval records,
              valuation information, accounting classification, and supporting
              documentation.
            </P>
            <Note testId="article-note-what-vs-why">
              A blockchain record shows what happened.
              <br />
              A financial record explains why it happened.
            </Note>
            <P>
              Unlike traditional payments, where transaction details are
              usually linked to account information and existing banking
              records, digital asset transactions often require additional
              internal processes to connect wallet activity with
              counterparties, documentation, and accounting treatment.
            </P>
          </Section>

          {/* 02 — Blockchain Data Finance Teams Need to Capture */}
          <Section id="blockchain-data-to-capture" index={2} title="Blockchain Data Finance Teams Need to Capture">
            <P>
              Digital asset transactions carry several data points that don't
              usually appear in traditional payment records. These details help
              finance teams verify transactions, reconcile activity, and
              maintain complete records.
            </P>
            <ul className="mt-4 space-y-3 pl-1">
              <Bullet lead="Transaction hash.">
                A unique identifier assigned to each blockchain transaction,
                allowing it to be independently verified and referenced during
                reconciliation or review.
              </Bullet>
              <Bullet lead="Wallet addresses.">
                The blockchain identifiers involved in the transfer. Unlike
                bank account details, wallet addresses don't automatically show
                the name or legal identity of their owner.
              </Bullet>
              <Bullet lead="Blockchain network.">
                The network used to process the transaction, such as Ethereum
                or Solana. The same digital asset can exist across multiple
                networks with entirely separate transaction histories and fee
                structures, which makes this detail more important than it
                might first appear.
              </Bullet>
              <Bullet lead="Timestamp.">
                The date and time the transaction was recorded on the
                blockchain.
              </Bullet>
              <Bullet lead="Asset type and amount.">
                The digital asset transferred and the quantity involved.
              </Bullet>
            </ul>
            <P>
              Together, these elements provide a verifiable record of the asset
              movement. For accounting purposes, they need to be combined with
              business information that explains the reason for the transaction
              and its role in company operations.
            </P>
          </Section>

          {/* 03 — Asset and Network Details */}
          <Section id="asset-and-network-details" index={3} title="Asset and Network Details in Digital Asset Records">
            <P>
              Digital assets aren't defined only by their name. The same asset
              can exist across multiple blockchain networks, and the network
              used for a given transaction affects how that activity gets
              tracked. USDC, for example, can be transferred on Ethereum,
              Solana, and several other networks. The asset name stays the
              same, but each transaction is recorded separately on its own
              blockchain, with its own fee structure and confirmation time.
            </P>
            <Note testId="article-note-asset-network">
              Recording both the asset and the network helps avoid incomplete
              transaction records and makes reconciliation easier when a
              company uses multiple wallets, payment providers, or settlement
              methods.
            </Note>
          </Section>

          {/* 04 — Building a Complete Record */}
          <Section id="building-a-complete-record" index={4} title="Building a Complete Digital Asset Transaction Record">
            <P>
              A blockchain transaction provides the technical evidence of a
              transfer. To become part of a company's financial records, it
              needs additional information that places the transaction in its
              business context.
            </P>
            <RecordTable
              rows={RECORD_COMPONENTS}
              headers={["Record Component", "What It Captures"]}
              testId="article-record-components-table"
              firstColWidth="w-[240px]"
            />
            <P>
              The exact fields required depend on a company's activities and
              reporting obligations, but a complete record typically connects
              blockchain data with operational and accounting information in
              something close to this structure.
            </P>
          </Section>

          {/* 05 — Valuing Digital Assets */}
          <Section id="valuing-digital-assets" index={5} title="Valuing Digital Assets in Financial Records">
            <P>
              One of the extra steps in digital asset accounting is determining
              the value of the asset at the moment of the transaction. Unlike a
              bank transfer in a fixed currency, a digital asset transaction
              requires its own valuation record: the company needs to establish
              the asset's value in its reporting currency and keep evidence of
              how that value was calculated.
            </P>
            <P>
              A payment of 5,000 USDC, for instance, is recorded on the
              blockchain as a transfer of units. The financial record also
              needs to capture the equivalent value used for accounting
              purposes at the time of the transaction, along with the amount
              and type of asset, the date and time, the exchange rate or
              valuation source used, and the method applied.
            </P>
            <Note testId="article-note-valuation">
              This becomes more important as transaction volume grows across
              different assets and networks. A consistent, well-documented
              valuation approach is what allows finance teams to reconcile
              activity, prepare financial statements, and support tax reporting
              later, when the original transaction is long past.
            </Note>
          </Section>

          {/* 06 — Financial Statements */}
          <Section id="financial-statements" index={6} title="How Digital Assets Are Recorded in Financial Statements">
            <P>
              Where a digital asset record ends up in the financial statements
              has changed recently, and the change is worth knowing in some
              detail. Until recently, US GAAP treated crypto assets as
              indefinite-lived intangible assets under ASC 350, which meant
              measuring them at historical cost and testing only for
              impairment. That model moved in one direction only: it captured a
              drop in value, and any recovery, even one that pushed the asset's
              price well above its original cost, simply went unrecorded, so
              many balance sheets ended up understating what companies actually
              held.
            </P>
            <P>
              The Financial Accounting Standards Board's ASU 2023-08, effective
              for fiscal years beginning after December 15, 2024, replaced that
              model for in-scope crypto assets. Companies now measure these
              assets at fair value each reporting period, with gains and losses
              flowing through net income rather than sitting unrecognized until
              a sale. The standard also requires crypto assets to be presented
              as a separate line on the balance sheet, distinct from other
              intangible assets, and requires the related fair value gains and
              losses to be broken out separately in the income statement. The
              rule applies to public companies, private companies, and
              not-for-profits alike.
            </P>
            <Note testId="article-note-fair-value">
              This has a direct implication for recordkeeping: fair value
              accounting only works if a company can reliably establish the
              value of each holding at the end of every reporting period, not
              just at the time of a transaction. That makes the valuation
              records described above a recurring requirement rather than a
              one-time calculation done at the moment of payment.
            </Note>
            <P>
              Outside the US, treatment still varies. Most jurisdictions
              following IFRS continue to account for crypto assets as
              intangible assets under IAS 38, without an equivalent
              fair-value-through-net-income model, which means the same holding
              can appear very differently on a US GAAP balance sheet than on an
              IFRS one. Businesses operating across both frameworks need
              records detailed enough to support either treatment.
            </P>
          </Section>

          {/* 07 — Recordkeeping and Retention */}
          <Section id="recordkeeping-and-retention" index={7} title="Recordkeeping and Retention Requirements">
            <P>
              Tax authorities in most jurisdictions expect digital asset
              records to be kept substantially longer than a single reporting
              cycle, and the exact period varies by country.
            </P>
            <ul className="mt-4 space-y-3 pl-1">
              <Bullet>
                The IRS generally expects records supporting a tax return to be
                kept for at least three years, extending to six years where
                income has been understated by more than 25%, and indefinitely
                in cases of unfiled returns or suspected fraud.
              </Bullet>
              <Bullet>
                The CRA requires supporting records to be kept for six years
                from the end of the last tax year they relate to.
              </Bullet>
              <Bullet>
                HMRC requires businesses to retain records for at least five
                years after the relevant filing deadline.
              </Bullet>
            </ul>
            <P>
              In each case, the expectation goes well beyond the transaction
              hash and wallet address a blockchain already provides. It's the
              full record described earlier, including valuation basis and
              business purpose, kept for as long as the relevant authority
              could reasonably ask to see it.
            </P>
          </Section>

          {/* 08 — Travel Rule */}
          <Section id="travel-rule" index={8} title="Compliance Recordkeeping: The Travel Rule">
            <P>
              A separate recordkeeping obligation comes from
              anti-money-laundering rules, and it applies well before a tax
              filing is ever due. Under the Travel Rule, virtual asset service
              providers are required to collect and transmit originator and
              beneficiary information for qualifying transfers, similar to the
              long-standing requirement for wire transfers. The threshold
              varies significantly by jurisdiction.
            </P>
            <ul className="mt-4 space-y-3 pl-1">
              <Bullet>
                The Financial Action Task Force recommends a threshold of
                USD/EUR 1,000
              </Bullet>
              <Bullet>
                The United States applies a higher threshold of USD 3,000 under
                the Bank Secrecy Act
              </Bullet>
              <Bullet>
                The European Union has applied a zero threshold to crypto
                transfers since its Transfer of Funds Regulation took effect in
                late 2024
              </Bullet>
              <Bullet>
                Singapore applies a threshold of SGD 1,500 under the Monetary
                Authority of Singapore's rules for digital payment token
                providers.
              </Bullet>
            </ul>
            <P>
              For a business making or receiving digital asset payments through
              a VASP, this means the counterparty information a finance team
              needs to collect for its own records, who the payment involves
              and under what relationship, often overlaps directly with
              information the VASP is separately required to collect for AML
              purposes. Building one clean record that satisfies both purposes
              is more efficient than treating them as two unrelated processes.
            </P>
          </Section>

          {/* 09 — Common Mistakes */}
          <Section id="common-mistakes" index={9} title="Common Mistakes in Digital Asset Recordkeeping">
            <P>
              A few recurring issues show up often enough to be worth naming
              directly.
            </P>
            <ul className="mt-4 space-y-3 pl-1">
              <Bullet lead="Network fees recorded inconsistently, or not at all.">
                Every blockchain transaction carries a network fee, paid in the
                asset used on that network, and it needs its own line in the
                record rather than being absorbed silently into the transaction
                amount.
              </Bullet>
              <Bullet lead="Valuation based on a daily average instead of the transaction-time price.">
                Digital asset prices can move meaningfully within a single day.
                Using an average or closing price instead of the value at the
                actual time of the transaction can distort both the transaction
                record and later tax reporting.
              </Bullet>
              <Bullet lead="Wallet addresses without a documented link to their owner.">
                A wallet address confirms that a transfer happened, but without
                an internal record connecting that address to a specific
                supplier, employee, or customer, the transaction becomes very
                difficult to explain later.
              </Bullet>
              <Bullet lead="Treating the same asset on different networks as identical.">
                As noted earlier, a token like USDC on Ethereum and the same
                token on Solana are recorded on entirely separate ledgers.
                Records that don't distinguish between them can understate or
                duplicate a company's actual holdings.
              </Bullet>
            </ul>
          </Section>

          {/* 10 — Practical Example */}
          <Section id="practical-example" index={10} title="Building the Record: A Practical Example">
            <P>
              Returning to the transaction that opened this guide, a complete
              record of that 5,000 USDC contractor payment would connect
              several pieces of information that never come from the blockchain
              alone.
            </P>
            <RecordTable
              rows={EXAMPLE_RECORD}
              headers={["Field", "Example Value"]}
              testId="article-example-record-table"
              firstColWidth="w-[240px]"
            />
            <P>
              None of these fields exist on the blockchain itself. Every one of
              them comes from the company's own financial processes, added on
              top of the transaction, not replacing it.
            </P>
            <Note testId="article-note-holds-up">
              That combination, verifiable blockchain data plus documented
              business context, is what allows a digital asset payment to hold
              up the same way a traditional payment does, in an audit, a
              reconciliation, or a tax filing years after the transaction
              itself has settled.
            </Note>
          </Section>
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Building Records That Hold Up
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-[880px]">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              This guide covered what belongs in a complete digital asset
              record: blockchain data, valuation, and retention requirements.
              WFII programs help finance professionals put this into practice,
              building the recordkeeping habits that hold up under audit and
              regulatory review.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticleRecordsGuidePage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
